/* ----------------------------------------
   Checks a LOT Excel against the product's column setup and explains every problem in plain words:
   { row, column, problem, fix }. (The LOT template to fill is made by the server: GET /api/products/:id/lot-template.)

   The expected layout is the Axis one: one row per person. The borrower's row comes first; each
   co-borrower gets their own row below it, with the same Reference No and the same case details.
---------------------------------------- */
import { formatDate, parseDate, resolveColumns } from "./lotUpload";

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";
const simplify = (text) => String(text).toLowerCase().replace(/[^a-z0-9]/g, "");
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const SAMPLE_DATE = { day: 15, month: 9, year: 2026 };

// Headers that show co-borrowers placed side by side in extra columns, e.g. "Co-applicant Name-1", "coborrower name 2"
const SIDE_BY_SIDE_HEADER = /co[\s_-]*(applicant|borrower)/i;

const quote = (value) => `"${String(value).trim()}"`;
// Why a column is required when the bank's Word files print it (config column neededBy, from the server)
const printedBy = (column) =>
  column.neededBy?.length ? ` The ${column.neededBy.join(", ")} print${column.neededBy.length > 1 ? "" : "s"} it.` : "";
const rowsList = (rows) => (rows.length > 6 ? `${rows.slice(0, 6).join(", ")} and ${rows.length - 6} more` : rows.join(", "));

function headerProblems(excelHeaders, config) {
  const problems = [];
  const { missing } = resolveColumns(excelHeaders, config);
  const used = new Set(config.columns.map((c) => simplify(c.excelColumn)));

  // Most important first: co-borrowers in extra columns instead of their own rows
  const sideBySide = excelHeaders.filter((h) => !used.has(simplify(h)) && SIDE_BY_SIDE_HEADER.test(String(h).trim()));
  if (sideBySide.length) {
    problems.push({
      row: 1,
      column: sideBySide.slice(0, 4).join(", "),
      problem: `Your Excel lists co-borrowers side by side in extra columns (${sideBySide.slice(0, 4).map(quote).join(", ")}${sideBySide.length > 4 ? ", …" : ""}). This upload needs each person on their own row.`,
      fix: "Put each co-borrower on a new row under the borrower's row, using the same columns as the borrower (name, address, mobile, PAN…). Copy the case details (Reference No, Lot No, loan details) onto that row too. Then delete the extra co-borrower columns. See the Example sheet of the LOT template.",
    });
  }

  missing.filter((column) => column.required).forEach((column) => {
    // A header that is close to the expected one (same letters ignoring spaces / dots / dashes / case)
    const similar = excelHeaders.find((h) => simplify(h).startsWith(simplify(column.excelColumn)) || simplify(column.excelColumn).startsWith(simplify(h)));
    problems.push({
      row: 1,
      column: column.excelColumn,
      problem: similar
        ? `The column ${quote(column.excelColumn)} (${column.label}) is spelt differently in your Excel: ${quote(similar)}.${printedBy(column)}`
        : `The column ${quote(column.excelColumn)} (${column.label}) is missing.${printedBy(column)}`,
      fix: similar
        ? `Rename the column ${quote(similar)} to ${quote(column.excelColumn)} exactly, in row 1.`
        : `Add a column named ${quote(column.excelColumn)} in row 1 and fill it in. The LOT template has the exact column names.`,
    });
  });

  // SheetJS renames repeated headers "Name", "Name_1", ...
  excelHeaders
    .filter((h) => /_\d+$/.test(h) && excelHeaders.includes(h.replace(/_\d+$/, "")))
    .forEach((h) =>
      problems.push({
        row: 1,
        column: h.replace(/_\d+$/, ""),
        problem: `The column ${quote(h.replace(/_\d+$/, ""))} appears more than once in row 1.`,
        fix: "Keep only one column with this name, or delete the extra one.",
      })
    );

  return problems;
}

function cellProblem(column, value, dateFormat, excelRow) {
  const text = String(value).trim();
  if (column.type === "number" && Number.isNaN(Number(text))) {
    const digits = text.match(/\d+/g);
    return {
      row: excelRow,
      column: column.header,
      problem: `${column.label} must be a plain number, but this cell says ${quote(text)}.`,
      fix: digits && digits.length === 1 ? `Write only the number: ${digits[0]}.` : "Write only the number, without letters, commas or symbols.",
    };
  }
  if (column.type === "date" && !parseDate(value, dateFormat)) {
    return {
      row: excelRow,
      column: column.header,
      problem: `${column.label} ${quote(text)} is not a date in the format ${dateFormat}.`,
      fix: `Write the date as ${dateFormat}, for example ${formatDate(SAMPLE_DATE, dateFormat)}. Check the day and month are real (e.g. no 31 for February).`,
    };
  }
  if (column.field === "Pan_no" && !PAN_PATTERN.test(text.toUpperCase())) {
    return {
      row: excelRow,
      column: column.header,
      problem: `PAN ${quote(text)} doesn't look like a PAN number.`,
      fix: "A PAN has 10 characters: 5 letters, 4 numbers, 1 letter, for example ABCDE1234F. Leave the cell empty if the PAN is not known.",
    };
  }
  if (column.field === "Type" && !["B", "C"].includes(text.toUpperCase())) {
    return {
      row: excelRow,
      column: column.header,
      problem: `Borrower Type must be B (borrower) or C (co-borrower), but this cell says ${quote(text)}.`,
      fix: "Write B on the borrower's row and C on each co-borrower's row.",
    };
  }
  return null;
}

// Rows of one case must sit together, start with the borrower and agree on the case details
function caseProblems(rows, resolved) {
  const problems = [];
  const ref = resolved.Reference_no;
  if (!ref) return problems;
  const firstRowOf = new Map();
  let previous = null;

  rows.forEach((row, index) => {
    const refNo = String(row[ref.header] ?? "").trim();
    if (!refNo) return;
    if (refNo !== previous && firstRowOf.has(refNo)) {
      problems.push({
        row: index + 2,
        column: ref.header,
        problem: `Reference No ${quote(refNo)} also appears on row ${firstRowOf.get(refNo) + 2}, with other cases in between.`,
        fix: "Keep all rows of one case together: the borrower's row first, then its co-borrowers right below it. If it's a different case, correct the Reference No.",
      });
    }
    if (!firstRowOf.has(refNo)) firstRowOf.set(refNo, index);
    previous = refNo;
  });

  // Consecutive rows with the same Reference No form one case (a later repeat is reported above)
  const blocks = [];
  rows.forEach((row, index) => {
    const refNo = String(row[ref.header] ?? "").trim();
    const last = blocks[blocks.length - 1];
    if (last && last.refNo === refNo) last.indexes.push(index);
    else if (refNo) blocks.push({ refNo, indexes: [index] });
  });

  const caseLevel = Object.values(resolved).filter((c) => c.group !== "BORROWER");
  blocks.forEach(({ refNo, indexes }) => {
    if (indexes.length < 2) return;
    const differing = caseLevel.filter((column) => {
      const filled = indexes.map((i) => String(rows[i][column.header] ?? "").trim()).filter(Boolean);
      return new Set(filled).size > 1;
    });
    if (differing.length) {
      const names = differing.map((c) => c.label);
      problems.push({
        row: indexes[0] + 2,
        column: differing.map((c) => c.header).slice(0, 4).join(", "),
        problem: `The rows of case ${quote(refNo)} (rows ${rowsList(indexes.map((i) => i + 2))}) have different ${names.length > 5 ? `${names.slice(0, 5).join(", ")} and ${names.length - 5} more` : names.join(", ")}.`,
        fix: "A co-borrower's row must repeat the case details exactly as on the borrower's row. Copy them from the borrower's row.",
      });
    }
    const type = resolved.Type;
    if (type) {
      const types = indexes.map((i) => String(rows[i][type.header] ?? "").trim().toUpperCase());
      if (types[0] === "C" || types.filter((t) => t === "B").length > 1) {
        problems.push({
          row: indexes[0] + 2,
          column: type.header,
          problem: `Case ${quote(refNo)} should have exactly one borrower (B), on its first row.`,
          fix: "Put the borrower's row first with Type B, and mark each co-borrower row below it with C.",
        });
      }
    }
  });
  return problems;
}

/**
 * Every problem in the file, in row order. An empty list means the file can be uploaded.
 * `unused` lists Excel columns that are not part of the product's setup (they're simply ignored);
 * `missingOptional` lists optional columns the Excel doesn't have (those details are left empty).
 */
export function checkLotExcel(rows, config) {
  if (!rows.length) {
    return { problems: [{ row: 1, column: "", problem: "The Excel has no data rows.", fix: "Fill in one row per person below the column names in row 1." }], unused: [], missingOptional: [] };
  }
  const excelHeaders = Object.keys(rows[0]);
  const problems = headerProblems(excelHeaders, config);
  const { resolved, missing } = resolveColumns(excelHeaders, config);

  rows.forEach((row, index) => {
    const excelRow = index + 2;
    Object.values(resolved).forEach((column) => {
      const value = row[column.header];
      if (isBlank(value)) {
        if (column.required) {
          problems.push({
            row: excelRow,
            column: column.header,
            problem: `${column.label} is empty.${printedBy(column)}`,
            fix: `Fill in ${column.label} on this row${column.group === "BORROWER" ? "" : " (copy it from the borrower's row if this is a co-borrower)"}.`,
          });
        }
        return;
      }
      const problem = cellProblem(column, value, config.dateFormat, excelRow);
      if (problem) problems.push(problem);
    });
  });

  problems.push(...caseProblems(rows, resolved));
  const used = new Set(Object.values(resolved).map((c) => c.header));
  return {
    // stable sort: row order, and within a row the order the checks found them (most important first)
    problems: problems.map((p, i) => ({ p, i })).sort((a, b) => a.p.row - b.p.row || a.i - b.i).map(({ p }) => p),
    unused: excelHeaders.filter((h) => !used.has(h) && !/^__EMPTY/.test(h)),
    missingOptional: missing.filter((column) => !column.required).map((column) => column.excelColumn),
  };
}
